import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Mail, ArrowRight } from 'lucide-react';
import { resourceAPI } from '../../services/api';

/* ─── Tab-Daten (entsprechen den 5 Abschnitten auf talentigo.de) ───────────── */
const TABS = [
  {
    id: 'stellenanalyse',
    label: 'Stellenanalyse',
    screenshot: '/talentigo-screenshots/tab1_stellenanalyse.png',
    title: 'Stellenanalyse & Kriterien definieren',
    bullets: [
      'Einschlusskriterien (K.O.-Kriterien) festlegen',
      'Weitere Kriterien mit individueller Gewichtung ergänzen',
      'Talentigo berechnet daraus automatisch einen Profil-Score',
    ],
  },
  {
    id: 'profile',
    label: 'Profile analysieren',
    screenshot: '/talentigo-screenshots/tab2_profile.png',
    title: 'Profile mit KI-Score auswerten',
    bullets: [
      'Profile werden automatisch gegen deine Kriterien gescort',
      'Begründung je Kriterium direkt sichtbar',
      'Filterung nach Mindest-Score möglich',
    ],
  },
  {
    id: 'antwortquote',
    label: 'Antwortquote steigern',
    screenshot: '/talentigo-screenshots/tab3_antwortquote.png',
    title: 'Personalisierte Ansprache generieren',
    bullets: [
      'KI erstellt profilbasierte Ansprache-Nachrichten',
      'Ton, Fokus und Länge einstellbar',
      'Erinnerungsfunktion für Follow-ups',
    ],
  },
  {
    id: 'ueberblick',
    label: 'Überblick behalten',
    screenshot: '/talentigo-screenshots/tab4_ueberblick.png',
    title: 'Pipeline & Kandidatenstatus',
    bullets: [
      'Kanban-Board mit Statusspalten (kontaktiert, Rückmeldung, …)',
      'Alle Kandidaten einer Stelle auf einen Blick',
      'Integration mit HubSpot möglich',
    ],
  },
  {
    id: 'analyse',
    label: 'Analyse vertiefen',
    screenshot: '/talentigo-screenshots/tab5_analyse.png',
    title: 'Gehalt & Unternehmensinfos',
    bullets: [
      'KI-gestützte Gehaltsschätzung je Kandidat',
      'Kununu-Bewertungen des eigenen Unternehmens direkt abrufbar',
      'Hilfreich für Gesprächsvorbereitung und Argumentation',
    ],
  },
];

/* ─── Hauptkomponente ─────────────────────────────────────────────────────── */
const KiRecruitingResource = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [manager, setManager] = useState(null);
  const [managerLoading, setManagerLoading] = useState(true);

  const currentTab = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  useEffect(() => {
    let cancelled = false;
    const loadManager = async () => {
      try {
        const { data } = await resourceAPI.getAccountManager();
        if (!cancelled && data?.assigned) setManager(data.manager);
      } catch {
        // lautlos
      } finally {
        if (!cancelled) setManagerLoading(false);
      }
    };
    loadManager();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">KI-Recruiting mit Talentigo</h1>
        <div className="w-12 h-1 rounded-full bg-hrf-coral mb-4" />
        <p className="text-gray-500 text-sm leading-relaxed max-w-2xl">
          Talentigo ist ein Active-Sourcing-Tool, das KI einsetzt, um passende Kandidat:innen
          aus öffentlichen Profilen (LinkedIn, XING u.&nbsp;a.) zu finden und anzusprechen.
          Unten siehst du, wie die einzelnen Schritte in der Oberfläche aussehen.
        </p>
      </div>

      {/* ── Produkt-Screenshots mit Tabs ── */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">

        {/* Tab-Leiste */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'shrink-0 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors duration-150 whitespace-nowrap',
                tab.id === activeTab
                  ? 'border-hrf-navy text-hrf-navy bg-hrf-navy/[0.03]'
                  : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Screenshot */}
        <div className="bg-gray-50 border-b border-gray-100">
          <img
            key={currentTab.id}
            src={currentTab.screenshot}
            alt={currentTab.title}
            className="w-full block"
            style={{ maxHeight: 480, objectFit: 'cover', objectPosition: 'top' }}
          />
        </div>

        {/* Beschreibung */}
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800 mb-3">{currentTab.title}</p>
            <ul className="space-y-2">
              {currentTab.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-gray-500">
                  <ArrowRight className="h-3.5 w-3.5 text-hrf-navy shrink-0 mt-0.5" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Schritt-Indikator */}
          <div className="flex sm:flex-col gap-1.5 shrink-0">
            {TABS.map((tab, i) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                title={tab.label}
                className={[
                  'w-2 h-2 rounded-full transition-colors duration-150',
                  tab.id === activeTab ? 'bg-hrf-navy' : 'bg-gray-200 hover:bg-gray-300',
                ].join(' ')}
                aria-label={`Tab ${i + 1}: ${tab.label}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA-Bereich ── */}
      <div
        className="rounded-2xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-5"
        style={{ backgroundColor: '#0d4993' }}
      >
        <div className="flex-1 min-w-0">
          {!managerLoading && manager ? (
            <>
              <p className="text-white font-semibold mb-0.5">Fragen? Dein Account Manager hilft weiter.</p>
              <div className="flex items-center gap-2.5 mt-3">
                <div className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {manager.initials || 'AM'}
                </div>
                <div className="leading-tight">
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Account Manager</p>
                  <p className="text-sm font-semibold text-white">{manager.display_name}</p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-white font-semibold">
              Interesse? Kostenlos testen oder deinen Account Manager fragen.
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/resources/frag-einen-expert')}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-hrf-navy text-sm font-semibold px-5 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <Mail className="h-4 w-4" />
            Account Manager kontaktieren
          </button>
          <a
            href="https://app.talentigo.de/de/accounts/signup/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-lg text-white text-sm font-semibold px-5 py-2.5 transition-colors"
            style={{ backgroundColor: '#fc4e48' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e03e38'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fc4e48'; }}
          >
            <ExternalLink className="h-4 w-4" />
            Kostenlos registrieren
          </a>
        </div>
      </div>

    </div>
  );
};

export default KiRecruitingResource;
